import type { CrewlinkClient } from '../supabase-client';
import type { AppUserRow } from '../database.types';

/**
 * Writer wrapper for `set_app_user_timezone` (migration 0027) — M12 / S12-00.
 *
 * The layering rule (ARCHITECTURE §2, INV-T02): UI → query hooks → writer
 * wrappers → Postgres RPC. This is the wrapper layer. UI code never calls
 * `.rpc(...)` directly and never calls `.update(...)` at all — and in this case
 * it could not: `authenticated` holds SELECT on `app_user` and nothing else.
 *
 * **THIS IS THE ONLY WAY THE CANONICAL TIMEZONE EVER CHANGES.** The other way
 * in, `ensure_app_user`, ignores the timezone it is handed once the profile
 * exists, so a travelling phone cannot move it (D-009). Nothing in Crewlink
 * calls this except a deliberate Settings save.
 *
 * `expected` is the timezone the screen was showing. The database updates only
 * while that is still the stored value, so a save decided against a stale read
 * is refused rather than landing on top of a newer one.
 *
 * The identity is taken from `auth.uid()` inside the function; it is never
 * passed from here (INV-T23).
 */
export async function setAppUserTimeZone(
  client: CrewlinkClient,
  expected: string,
  timezone: string,
): Promise<AppUserRow> {
  const { data, error } = await client.rpc('set_app_user_timezone', {
    p_expected_timezone: expected,
    p_timezone: timezone,
  });

  if (error) throw error;
  if (!data) throw new Error('set_app_user_timezone returned no profile row');

  return data;
}

/**
 * What went wrong, in a sentence Brandon can act on.
 *
 * **THE CODES COME FROM THE WRITER, NOT FROM PARSING PROSE.** `CL030` is a
 * blank value, `CL031` is "that is not a timezone Crewlink can store", and
 * `55000` is the stale-write guard — the one case where the honest answer is
 * "somebody else changed it, look again" rather than "try again".
 */
export function describeTimeZoneSaveFailure(error: unknown): string {
  // Only STRINGS are read. A code or message that arrives as some other shape
  // is ignored rather than stringified into `[object Object]` and searched.
  const text = (value: unknown): string => (typeof value === 'string' ? value : '');
  const raw = error as { code?: unknown; message?: unknown } | null;
  const detail = `${text(raw?.code)} ${text(raw?.message)}`;

  if (detail.includes('CL030')) return 'Choose a time zone first.';
  if (detail.includes('CL031')) {
    return 'Crewlink could not use that time zone. Pick one from the list.';
  }
  if (detail.includes('55000')) {
    return 'Your saved time zone changed somewhere else. Reopen Settings and try again.';
  }
  if (detail.includes('P0002')) return 'Crewlink has no profile for you yet.';
  return 'Crewlink could not save your time zone. Check your connection and try again.';
}

/**
 * Was this the STALE-WRITE refusal specifically?
 *
 * It is the one failure where the screen itself is now known to be wrong: the
 * saved value moved somewhere else, so the caller must re-read before telling
 * anybody to look again. Every other refusal leaves the saved value exactly
 * where it was.
 */
export function isStaleTimeZoneSave(error: unknown): boolean {
  const text = (value: unknown): string => (typeof value === 'string' ? value : '');
  const raw = error as { code?: unknown; message?: unknown } | null;
  return `${text(raw?.code)} ${text(raw?.message)}`.includes('55000');
}
